/* eslint-disable sonarjs/cognitive-complexity -- it's a complex component */
import { useEffect, useMemo, useRef } from "react";
import { cx } from "shared/utils";
import { Button, ButtonVariant, Show } from "shared/components";
import {
  ActivityItem,
  AdjustedApplicantFeeActivity,
  AdjustedApplicationRentActivity,
  ApplicationActivity,
  VerifiedIncomeAdjustedActivity,
  IdvDataUpdatedActivity,
  DiscountActivity,
  RoleChangedActivity,
  SplitApplicationActivity,
  VerificationTemplateOverriddenActivity,
  VerificationTemplateChangedActivity,
  TemplateRouteAnsweredActivity,
  ApplicationMergedActivity,
  ApplicationMovedActivity,
  ApplicationAssignedActivity,
  FeeRefundedActivity,
  DesiredMoveInDateUpdatedActivity,
  ReportRerunActivity,
  CriminalStatewideSearchActivity,
  CombinedIncomeRerunRequestedActivity,
  HousingAssistanceAdjustedActivity,
  DesiredLeaseTermUpdatedActivity,
  PrimaryApplicantChangedActivity,
  ScreeningStatusChangedActivity,
  ItemizedReceiptSentActivity,
  IncomeOverrideActivity,
  RentalHistoryAddressAddedActivity
} from "./types";

import moment from "moment";
import {
  AGENT_ACTIVITY_TYPES,
  applicationDecisionStatus,
  combinedRoles
} from "shared/app/ActivityLog/constants";
import { ApplicantType } from "shared/types";
import { ApplicantRoleLabel } from "shared/utils";
import {
  getActivityMessage,
  getAuthorText,
  getContactWithRoleText,
  type ActivityApplicant
} from "./helpers";
import { getFullName } from "shared/utils/get-full-name";
import { RoleTitles } from "views/settings-users/slices/users/constants";
import { formatToCurrency } from "shared/utils";
import { CombinedIncomeOverrides } from "./components/CombinedIncomeOverrides";
import { IncomeAdjustmentView } from "./views/IncomeAdjustmentView";
import { LandlordSurveyView } from "./views/LandlordSurveyView";
import { PremiumRentalVerificationSurveyView } from "./views/PremiumRentalVerificationSurveyView";
import { EmployerVerificationSurveyView } from "./views/EmployerVerificationSurveyView";
import { RemoveCriminalReportView } from "./views/RemoveCriminalReportView";
import {
  IncomeReverifyRequestCanceled,
  IncomeReverifyRequestCompleted,
  IncomeReverifyRequestCreated,
  IncomeReverifyRequestFailed,
  ReverifyCanceled,
  ReverifyCompleted,
  ReverifyRequested
} from "./components/ReverifyItems";
import { Link, useSearchParams } from "react-router-dom";
import { IdvDataUpdated } from "./components/IdvDataUpdated";
import { IdentityVerificationOverride } from "./components/IdentityVerificationOverride";
import { WarningsReportOverride } from "./components/WarningsReportOverride";
import { IdentityVerificationRerun } from "./components/IdentityVerificationRerun";
import { Card } from "shared/components/Card";
import { getDiscountCodeName } from "utils/discount-codes";
import { ApplicationSplit } from "./components/ApplicationSplit";
import { AdjustedApplicationRentLog } from "./components/AdjustedApplicationRentLog";
import { AdjustedApplicantFeeOverrides } from "./components/AdjustedApplicantFeeOverrides";
import { useAuthContext } from "providers/Auth";
import { VerificationTemplateOverride } from "./components/VerificationTemplateOverride";
import { VerificationTemplateChanged } from "./components/VerificationTemplateChanged";
import { TemplateRouteAnswered } from "./components/TemplateRouteAnswered";
import { ApplicationMerged } from "./components/ApplicationMerged";
import { ApplicationStageChanged } from "./components/ApplicationStageChanged";
import { ApplicationCanceled } from "./components/ApplicationCanceled";
import { FeeRefunded } from "./components/FeeRefunded";
import { ItemizedReceiptSent } from "./components/ItemizedReceiptSent";
import {
  AgentActivityCard,
  type AgentActivity
} from "./components/AgentActivityCard";
import { ReportRerun } from "./components/ReportRerun";
import { CriminalStatewideSearch } from "./components/CriminalStatewideSearch";
import { HousingAssistanceAdjusted } from "./components/HousingAssistanceAdjusted";
import { PrimaryApplicantChanged } from "./components/PrimaryApplicantChanged";
import { getIncomeJobName } from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils";
import { IncomeOverride } from "./components/IncomeOverride";
import { RentalHistoryAddressAdded } from "./components/RentalHistoryAddressAdded";
import { CombinedIncomeReportRerun } from "./components/CombinedIncomeReportRerun";
import { ManuallyAddedIncomeEvent } from "./components/ManuallyAddedIncome";
import { UnderwritingAgentCheckCard } from "./components/UnderwritingAgentCheckCard";
import { ScreeningStatusChanged } from "./components/ScreeningStatusChanged";

interface ActivityNoteProps {
  activityItem: ActivityItem;
  applicationId?: string;
  applicants?: ActivityApplicant[];
  closeActivityLog?: () => void;
}

export const ActivityNote = ({
  activityItem,
  applicationId,
  applicants,
  closeActivityLog
}: ActivityNoteProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const ref = useRef<HTMLDivElement>(null);
  const { type } = useAuthContext();

  const isFocused = useMemo(
    () =>
      Boolean(
        "note_id" in activityItem &&
        searchParams.get("note_id") === String(activityItem.note_id)
      ),
    [searchParams]
  );

  useEffect(() => {
    const isFocused = Boolean(
      "note_id" in activityItem &&
      searchParams.get("note_id") === String(activityItem.note_id)
    );
    if (!isFocused || !ref.current) return;
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    setTimeout(() => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("note_id");
          return newParams;
        },
        { replace: true }
      );
    }, 1000);
  }, []);

  const message = getActivityMessage(activityItem);

  return (
    <div className="animate-appear w-full max-w-[550px]" ref={ref}>
      <Card
        className={cx(
          "p-6 w-full bg-white transition-all duration-300 ease-in-out ring-2 ring-transparent transform",
          isFocused ? "!ring-primary-200 scale-105" : "scale-100"
        )}
      >
        <div className="flex justify-between relative mb-2">
          {activityItem.activity_type === "verification_override" ||
          activityItem.activity_type === "income_override" ? (
            <CombinedIncomeOverrides activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type === "income_reverify" ? (
            <ReverifyRequested activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type === "income_reverify_canceled" ? (
            <ReverifyCanceled activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type === "income_reverify_completed" ? (
            <ReverifyCompleted activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type === "income_reverify_request_created" ? (
            <IncomeReverifyRequestCreated activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type ===
          "income_reverify_request_cancelled" ? (
            <IncomeReverifyRequestCanceled activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type ===
          "income_reverify_request_completed" ? (
            <IncomeReverifyRequestCompleted activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type === "income_reverify_request_failed" ? (
            <IncomeReverifyRequestFailed activityItem={activityItem} />
          ) : null}

          {activityItem.activity_type === "income_added_manually" ||
          activityItem.activity_type === "manual_income_canceled" ? (
            <ManuallyAddedIncomeEvent activityItem={activityItem} />
          ) : null}

          <Show if={activityItem.activity_type === "resumed"}>
            <p className="text-base font-bold text-content-1000">
              Application was resumed from hold
            </p>
          </Show>

          <Show if={activityItem.activity_type === "placed_on_hold"}>
            <p className="text-base font-bold text-content-1000">
              Application was placed on hold
            </p>
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "complete_request_retry_identity_verification"
            }
          >
            <div className="space-y-2">
              <p className="text-base font-bold text-content-1000">
                Identity verification retry was completed{" "}
                <span className="font-normal">
                  by {activityItem.author?.name} (
                  {activityItem.author?.author_role
                    ? combinedRoles[activityItem.author.author_role]
                    : "-"}
                  )
                </span>
              </p>
            </div>
          </Show>

          <Show if={activityItem.activity_type === "idv_data_updated"}>
            <IdvDataUpdated
              activityItem={activityItem as IdvDataUpdatedActivity}
            />
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "cancel_request_retry_identity_verification"
            }
          >
            <div className="space-y-2">
              <p className="text-base font-bold text-content-1000">
                Identity verification retry request was canceled{" "}
                <span className="font-normal">
                  by {activityItem.author?.name}
                </span>
              </p>
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              "contact" in activityItem.details &&
              activityItem.details.contact ? (
                <p className="text-sm font-normal text-content-400">
                  For{" "}
                  {getFullName({
                    firstName: activityItem.details.contact.first_name,
                    lastName: activityItem.details.contact.last_name,
                    middleName: activityItem.details.contact.middle_name,
                    short: true
                  })}{" "}
                  (
                  {"role" in activityItem.details.contact &&
                  activityItem.details.contact.role
                    ? combinedRoles[activityItem.details.contact.role]
                    : "-"}
                  )
                </p>
              ) : null}
            </div>
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "request_retry_identity_verification"
            }
          >
            <div className="space-y-2">
              <p className="text-base font-bold text-content-1000">
                Identity verification request was re-sent{" "}
                <span className="font-normal">
                  by {activityItem.author?.name}
                </span>
              </p>
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              "contact" in activityItem.details &&
              activityItem.details.contact ? (
                <p className="text-sm font-normal text-content-400">
                  For{" "}
                  {getFullName({
                    firstName: activityItem.details.contact.first_name,
                    lastName: activityItem.details.contact.last_name,
                    middleName: activityItem.details.contact.middle_name,
                    short: true
                  })}{" "}
                  (
                  {"role" in activityItem.details.contact &&
                  activityItem.details.contact.role
                    ? combinedRoles[activityItem.details.contact.role]
                    : "-"}
                  )
                </p>
              ) : null}
            </div>
          </Show>

          <Show
            if={
              activityItem.activity_type === "canadian_criminal_report_invited"
            }
          >
            <p className="text-base font-bold text-content-1000">
              Canadian criminal background check email was sent
            </p>
          </Show>
          <Show
            if={
              activityItem.activity_type ===
              "failed_identity_verification_manual_override"
            }
          >
            <IdentityVerificationOverride
              activityItem={activityItem as ApplicationActivity}
            />
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "failed_warnings_report_manual_override"
            }
          >
            <WarningsReportOverride
              activityItem={activityItem as ApplicationActivity}
            />
          </Show>

          <Show
            if={[
              "identity_verification_rerun_requested",
              "identity_verification_rerun_completed",
              "identity_verification_rerun_canceled",
              "identity_verification_bypass_requested",
              "identity_verification_bypass_approved",
              "identity_verification_bypass_declined"
            ].includes(activityItem.activity_type)}
          >
            <IdentityVerificationRerun
              activityItem={activityItem as ApplicationActivity}
            />
          </Show>

          {activityItem.activity_type === "role_changed" ? (
            <p className="text-base font-bold text-content-1000">
              Role changed{" "}
              <span className="font-normal">
                {activityItem.details.contact.first_name}{" "}
                {activityItem.details.contact.last_name} is now a{" "}
                {
                  ApplicantRoleLabel[
                    (activityItem as RoleChangedActivity).details
                      .new_role as ApplicantType
                  ]
                }
              </span>
            </p>
          ) : null}

          {activityItem.activity_type === "primary_applicant_changed" ? (
            <PrimaryApplicantChanged
              activityItem={activityItem as PrimaryApplicantChangedActivity}
            />
          ) : null}

          {activityItem.activity_type === "screening_status_changed" ? (
            <ScreeningStatusChanged
              activityItem={activityItem as ScreeningStatusChangedActivity}
            />
          ) : null}

          {activityItem.activity_type === "name_changed" ? (
            <div className="space-y-2">
              <p className="text-base font-bold text-content-1000">
                Name changed{" "}
                <span className="font-normal">
                  for{" "}
                  {getFullName({
                    firstName: activityItem.details.new_name.first_name,
                    lastName: activityItem.details.new_name.last_name,
                    middleName: activityItem.details.new_name.middle_name,
                    short: true
                  })}{" "}
                  {activityItem.details.contact.role
                    ? `(${combinedRoles[activityItem.details.contact.role]})`
                    : null}
                </span>
              </p>
              <div className="text-sm font-normal text-content-400">
                <p>
                  <strong>Before:</strong>{" "}
                  {getFullName({
                    firstName: activityItem.details.previous_name.first_name,
                    lastName: activityItem.details.previous_name.last_name,
                    middleName: activityItem.details.previous_name.middle_name,
                    short: true
                  })}
                </p>
                <p className="mt-1">
                  <strong>After:</strong>{" "}
                  {getFullName({
                    firstName: activityItem.details.new_name.first_name,
                    lastName: activityItem.details.new_name.last_name,
                    middleName: activityItem.details.new_name.middle_name,
                    short: true
                  })}
                </p>
              </div>
            </div>
          ) : null}

          {activityItem.activity_type === "invited" ? (
            <p className="text-base font-bold text-content-1000">
              {activityItem.details.contact?.first_name
                ? getFullName({
                    firstName: activityItem.details.contact.first_name || "",
                    lastName: activityItem.details.contact.last_name || "",
                    middleName: activityItem.details.contact.middle_name,
                    short: true
                  })
                : activityItem.details.email}{" "}
              <span className="font-normal">
                invited as{" "}
                {activityItem.details.invitee_role === ApplicantType.CoSigner
                  ? "a "
                  : "an "}
                {ApplicantRoleLabel[activityItem.details.invitee_role]}
              </span>
            </p>
          ) : null}

          {activityItem.activity_type === "manual_application_started" ? (
            <p className="text-base text-content-1000">
              <span className="font-bold">Manual application was started</span>{" "}
              for{" "}
              {getFullName({
                firstName: activityItem.details?.contact?.first_name ?? "",
                lastName: activityItem.details?.contact?.last_name ?? "",
                middleName: activityItem.details?.contact?.middle_name
              })}
            </p>
          ) : null}

          {activityItem.activity_type === "resent_invite" ? (
            <div className="flex flex-col gap-1">
              <p className="text-base font-bold text-content-1000">
                Invitation was resent
              </p>
              <p className="text-sm font-normal text-content-400">
                {activityItem.details.email} (
                {activityItem.details.invitee_role === ApplicantType.CoSigner
                  ? "a "
                  : "an "}
                <span className="lowercase">
                  {ApplicantRoleLabel[activityItem.details.invitee_role]}
                </span>
                ) was emailed an invitation
              </p>
            </div>
          ) : null}
          <Show if={activityItem.activity_type === "group_submitted"}>
            <p className="text-base font-bold text-content-1000">
              Application is pending a decision
            </p>
          </Show>
          <Show if={activityItem.activity_type === "decision_email_sent"}>
            <p className="text-base font-bold text-content-1000">
              Decision email was sent
            </p>
          </Show>
          <Show if={activityItem.activity_type === "undo_decision_email_sent"}>
            <p className="text-base font-bold text-content-1000">
              Undo email was sent
            </p>
          </Show>
          <Show if={activityItem.activity_type === "undo_decision"}>
            <p className="text-base font-bold text-content-1000">
              Decision was undone
            </p>
          </Show>
          {activityItem.activity_type ===
          "partner_requested_additional_document" ? (
            <div className="flex flex-col gap-1">
              <p className="text-base font-bold text-content-1000">
                Additional document request was sent
                {activityItem.author?.name ? (
                  <span className="font-normal text-content-400">
                    {" "}
                    by {activityItem.author.name}
                    {activityItem.author.author_role
                      ? ` (${combinedRoles[activityItem.author.author_role]})`
                      : ""}
                  </span>
                ) : null}
              </p>
              {activityItem.details?.contact ? (
                <p className="text-sm font-normal text-content-400">
                  to{" "}
                  {getContactWithRoleText({
                    contact: activityItem.details.contact,
                    applicants,
                    applicantId: activityItem.details.applicant_id
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
          {activityItem.activity_type ===
          "partner_canceled_additional_document_request" ? (
            <div className="flex flex-col gap-1">
              <p className="text-base font-bold text-content-1000">
                Additional document request was canceled
                {activityItem.author?.name ? (
                  <span className="font-normal text-content-400">
                    {" "}
                    by {activityItem.author.name}
                    {activityItem.author.author_role
                      ? ` (${combinedRoles[activityItem.author.author_role]})`
                      : ""}
                  </span>
                ) : null}
              </p>
              {activityItem.details?.contact ? (
                <p className="text-sm font-normal text-content-400">
                  for{" "}
                  {getContactWithRoleText({
                    contact: activityItem.details.contact,
                    applicants,
                    applicantId: activityItem.details.applicant_id
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
          {activityItem.activity_type ===
          "applicant_uploaded_additional_document" ? (
            <div className="flex flex-col gap-1">
              <p className="text-base font-bold text-content-1000">
                New documents received
                {activityItem.details?.contact ? (
                  <span className="font-normal text-content-400">
                    {" "}
                    from{" "}
                    {getContactWithRoleText({
                      contact: activityItem.details.contact,
                      applicants,
                      applicantId: activityItem.details.applicant_id
                    })}
                  </span>
                ) : null}
              </p>

              {applicationId && activityItem.details?.applicant_id ? (
                <Link
                  to={`/applications/${applicationId}/reports?applicantId=${activityItem.details.applicant_id}&report=additional_documents`}
                  onClick={() => closeActivityLog?.()}
                >
                  <Button variant={ButtonVariant.TEXT}>View documents</Button>
                </Link>
              ) : null}
            </div>
          ) : null}
          <Show if={activityItem.activity_type === "workflow_email_sent"}>
            <div className="space-y-1">
              <p className="text-base font-bold text-content-1000">
                Workflow email was sent
              </p>
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              "email_template_name" in activityItem.details &&
              activityItem.details.email_template_name ? (
                <p className="text-sm font-normal text-content-400">
                  Template: {activityItem.details.email_template_name}
                </p>
              ) : null}
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              "sent_count" in activityItem.details &&
              activityItem.details.sent_count ? (
                <p className="text-sm font-normal text-content-400">
                  Sent to {activityItem.details.sent_count} recipient
                  {activityItem.details.sent_count === 1 ? "" : "s"}
                  {"failed_count" in activityItem.details &&
                  activityItem.details.failed_count
                    ? `, ${activityItem.details.failed_count} failed`
                    : ""}
                </p>
              ) : null}
            </div>
          </Show>
          <Show if={activityItem.activity_type === "workflow_sms_sent"}>
            <div className="space-y-1">
              <p className="text-base font-bold text-content-1000">
                Workflow SMS was sent
              </p>
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              "template_name" in activityItem.details &&
              activityItem.details.template_name ? (
                <p className="text-sm font-normal text-content-400">
                  Template: {activityItem.details.template_name}
                </p>
              ) : null}
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              "sent_count" in activityItem.details &&
              activityItem.details.sent_count ? (
                <p className="text-sm font-normal text-content-400">
                  Sent to {activityItem.details.sent_count} recipient
                  {activityItem.details.sent_count === 1 ? "" : "s"}
                  {"failed_count" in activityItem.details &&
                  activityItem.details.failed_count
                    ? `, ${activityItem.details.failed_count} failed`
                    : ""}
                </p>
              ) : null}
            </div>
          </Show>
          <Show if={activityItem.activity_type === "credit_report_sent"}>
            <p className="text-base font-bold text-content-1000">
              Credit report was automatically emailed
            </p>
          </Show>
          <Show if={activityItem.activity_type === "compliance_report_sent"}>
            <p className="text-base font-bold text-content-1000">
              Compliance report was automatically emailed
            </p>
          </Show>
          <Show if={activityItem.activity_type === "itemized_receipt_sent"}>
            <p className="text-base font-bold text-content-1000">
              Itemized receipt was sent
            </p>
          </Show>
          {activityItem.activity_type === "report_run_manually" ? (
            <p className="text-base font-bold text-content-1000">
              {activityItem.details?.verification_title} was run manually{" "}
              <span className="font-normal">
                for{" "}
                {getFullName({
                  firstName: activityItem.details?.contact?.first_name || "",
                  lastName: activityItem.details?.contact?.last_name || "",
                  middleName: activityItem.details?.contact?.middle_name,
                  short: true
                })}
              </span>
            </p>
          ) : null}
          <Show if={activityItem.activity_type === "verified_income_adjusted"}>
            <IncomeAdjustmentView
              activityItem={activityItem as VerifiedIncomeAdjustedActivity}
            />
          </Show>
          <Show if={activityItem.activity_type === "submitted"}>
            <p className="text-base font-bold text-content-1000">
              {activityItem.author?.name} ({activityItem.author?.email}){" "}
              <span className="font-normal">submitted their application</span>
            </p>
          </Show>

          <Show if={activityItem.activity_type === "requested_pet_screening"}>
            <p className="text-base font-bold text-content-1000">
              <p>Animal screening email was sent </p>
              <span className="text-sm font-normal text-content-400">
                {activityItem.author?.name} ({activityItem.author?.email}) was
                emailed an animal screening link
              </span>
            </p>
          </Show>

          <Show if={activityItem.activity_type === "decision_made"}>
            <p className="text-base font-bold text-content-1000">
              {
                applicationDecisionStatus[
                  (activityItem as ApplicationActivity).details?.status || ""
                ]
              }
            </p>
          </Show>
          <Show if={activityItem.activity_type === "auto_approved"}>
            <p className="text-base font-bold text-content-1000">
              Application automatically approved
            </p>
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "push_applicants_to_property_management_system"
            }
          >
            <p className="text-base font-bold text-content-1000">
              {typeof activityItem.details === "object" &&
              "pms_push_status" in activityItem.details &&
              activityItem.details.pms_push_status === "success"
                ? "Application was pushed to Property Management System"
                : "Failed to push application to Property Management System"}
            </p>
          </Show>

          <Show
            if={activityItem.activity_type === "appfolio_guest_card_created"}
          >
            <p className="text-base font-bold text-content-1000">
              {typeof activityItem.details === "object" &&
              "guest_card_status" in activityItem.details &&
              activityItem.details.guest_card_status === "success"
                ? "AppFolio guest card was created"
                : "Failed to create AppFolio guest card"}
              {typeof activityItem.details === "object" &&
              "guest_card_name" in activityItem.details &&
              activityItem.details.guest_card_name ? (
                <span className="font-normal">
                  {" "}
                  for {activityItem.details.guest_card_name}
                </span>
              ) : null}
            </p>
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "appfolio_guest_card_already_exists"
            }
          >
            <p className="text-base font-bold text-content-1000">
              AppFolio guest card already existed
              {typeof activityItem.details === "object" &&
              "guest_card_name" in activityItem.details &&
              activityItem.details.guest_card_name ? (
                <span className="font-normal">
                  {" "}
                  for {activityItem.details.guest_card_name}
                </span>
              ) : null}
            </p>
          </Show>

          <Show
            if={activityItem.activity_type === "appfolio_guest_card_updated"}
          >
            <p className="text-base font-bold text-content-1000">
              {typeof activityItem.details === "object" &&
              "guest_card_status" in activityItem.details &&
              activityItem.details.guest_card_status === "success"
                ? "AppFolio guest card was updated"
                : "Failed to update AppFolio guest card"}
              {typeof activityItem.details === "object" &&
              "guest_card_name" in activityItem.details &&
              activityItem.details.guest_card_name ? (
                <span className="font-normal">
                  {" "}
                  for {activityItem.details.guest_card_name}
                </span>
              ) : null}
            </p>
          </Show>

          <Show if={activityItem.activity_type === "discount_applied"}>
            <p className="text-base font-bold text-content-1000">
              Discount applied{" "}
              <span className="font-normal">
                by {activityItem.author?.name}
                {activityItem.author?.author_role
                  ? ` (${combinedRoles[activityItem.author.author_role]})`
                  : ""}
              </span>
            </p>
          </Show>

          <Show if={activityItem.activity_type === "discount_removed"}>
            <p className="text-base font-bold text-content-1000">
              Discount canceled{" "}
              <span className="font-normal">
                by {activityItem.author?.name}
                {activityItem.author?.author_role
                  ? ` (${combinedRoles[activityItem.author.author_role]})`
                  : ""}
              </span>
            </p>
          </Show>

          <Show
            if={activityItem.activity_type === "verification_template_changed"}
          >
            <p className="text-base font-bold text-content-1000">
              Screening template was changed
            </p>
          </Show>

          <Show if={activityItem.activity_type === "template_route_answered"}>
            <p className="text-base font-bold text-content-1000">
              Template routing question answered
            </p>
          </Show>

          <Show
            if={
              activityItem.activity_type === "fee_refunded" &&
              "fee_type" in activityItem.details
            }
          >
            <FeeRefunded activity={activityItem as FeeRefundedActivity} />
          </Show>

          <Show
            if={activityItem.activity_type === "credit_report_rerun_requested"}
          >
            <ReportRerun
              activity={activityItem as ReportRerunActivity}
              reportName="Credit report"
            />
          </Show>

          <Show
            if={[
              "criminal_statewide_search_requested",
              "criminal_statewide_search_received"
            ].includes(activityItem.activity_type)}
          >
            <CriminalStatewideSearch
              activity={activityItem as CriminalStatewideSearchActivity}
            />
          </Show>

          <Show
            if={[
              "combined_income_rerun_requested",
              "combined_income_rerun_canceled",
              "combined_income_rerun_completed",
              "new_income_request_created",
              "new_income_request_canceled",
              "new_income_request_completed"
            ].includes(activityItem.activity_type)}
          >
            <CombinedIncomeReportRerun
              activity={activityItem as CombinedIncomeRerunRequestedActivity}
            />
          </Show>
        </div>

        <Show if={activityItem.activity_type === "auto_approved"}>
          <p className="text-sm font-normal text-content-400">by Boom</p>
        </Show>

        {activityItem.activity_type === "remove_criminal_report" ? (
          <RemoveCriminalReportView
            activityItem={activityItem as ApplicationActivity}
          />
        ) : null}
        {activityItem.activity_type === "invite_canceled" ? (
          <p className="text-base font-bold text-content-1000">
            Invitation was canceled for{" "}
            <span className="font-normal">
              {activityItem.details.contact?.email}
            </span>
          </p>
        ) : null}

        {activityItem.activity_type === "canceled" ||
        activityItem.activity_type === "uncanceled" ? (
          <ApplicationCanceled
            activityItem={activityItem as ApplicationActivity}
          />
        ) : null}

        <Show if={activityItem.activity_type === "income_items_assigned"}>
          <p className="text-base font-bold text-content-1000">
            Income transactions{" "}
            <span className="font-normal">
              were edited by {activityItem?.author?.name}
            </span>
          </p>
        </Show>

        <Show
          if={[
            "landlord_survey_sent",
            "landlord_survey_first_nudge",
            "landlord_survey_second_nudge",
            "landlord_survey_final_nudge"
          ].includes(activityItem.activity_type)}
        >
          <p className="text-base font-bold text-content-1000">
            Rental verification survey was sent
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type === "premium_rental_history_survey_sent"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Rental verification survey was sent
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "premium_rental_history_survey_resent"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Reminder was sent (automatic)
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "premium_rental_history_survey_manually_sent"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Rental verification survey was manually re-sent
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "premium_rental_history_survey_landlord_details_updated"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Rental verification landlord details were updated
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "premium_rental_history_survey_log_outreach"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Outreach was logged for rental verification
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "premium_rental_history_survey_case_closed"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Rental verification case was closed
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "premium_rental_history_survey_declined"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Rental verification survey was declined
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "premium_rental_history_survey_skipped"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Survey skipped by Boom
          </p>
        </Show>

        {/* BSCPF-6584. Employer verification timeline entries. */}
        <Show
          if={
            activityItem.activity_type === "employment_verification_survey_sent"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Employer verification was sent
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "employment_verification_survey_resent"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Employer verification was re-sent
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "employment_verification_supervisor_details_updated"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Supervisor details were updated
          </p>
        </Show>

        <Show
          if={
            activityItem.activity_type ===
            "employment_verification_survey_declined"
          }
        >
          <p className="text-base font-bold text-content-1000">
            Employer verification was declined
          </p>
        </Show>

        {activityItem.activity_type === "employment_verification_survey_sent" ||
        activityItem.activity_type ===
          "employment_verification_survey_resent" ||
        activityItem.activity_type ===
          "employment_verification_supervisor_details_updated" ||
        activityItem.activity_type ===
          "employment_verification_survey_declined" ? (
          <EmployerVerificationSurveyView activityItem={activityItem} />
        ) : null}

        <Show if={activityItem.activity_type === "housing_history_added"}>
          <RentalHistoryAddressAdded
            activityItem={activityItem as RentalHistoryAddressAddedActivity}
          />
        </Show>

        {activityItem.activity_type === "premium_rental_history_survey_sent" ||
        activityItem.activity_type === "premium_rental_history_survey_resent" ||
        activityItem.activity_type ===
          "premium_rental_history_survey_manually_sent" ||
        activityItem.activity_type ===
          "premium_rental_history_survey_landlord_details_updated" ||
        activityItem.activity_type ===
          "premium_rental_history_survey_log_outreach" ||
        activityItem.activity_type ===
          "premium_rental_history_survey_case_closed" ||
        activityItem.activity_type ===
          "premium_rental_history_survey_declined" ||
        activityItem.activity_type ===
          "premium_rental_history_survey_skipped" ? (
          <PremiumRentalVerificationSurveyView activityItem={activityItem} />
        ) : null}

        {activityItem.activity_type === "landlord_survey_manual_resent" ? (
          <>
            <p className="text-base font-bold text-content-1000">
              Rental verification survey was re-sent to a new email
            </p>
            {typeof activityItem.details === "object" && (
              <p className="text-sm font-normal text-content-1000">
                by{" "}
                {activityItem.details.rental_verification_survey?.sender?.name}
                {activityItem.details.rental_verification_survey?.sender
                  ?.role &&
                  `, ${RoleTitles[activityItem.details.rental_verification_survey.sender.role]}`}
              </p>
            )}
          </>
        ) : null}

        {activityItem.details ? (
          <>
            <Show if={activityItem.activity_type === "group_submitted"}>
              <p className="text-sm font-normal text-content-400">
                All applicants have submitted their rental applications
              </p>
            </Show>

            {activityItem.activity_type === "income_items_assigned" ? (
              <p className="text-sm font-normal text-content-400">
                For {activityItem.details?.applicant_name} — Income source:{" "}
                {getIncomeJobName(
                  activityItem.details?.income?.additional_data
                )}
              </p>
            ) : null}

            <Show if={message}>
              <p className="text-sm font-normal text-content-400">{message}</p>
            </Show>

            <Show if={activityItem.activity_type === "undo_decision"}>
              {getAuthorText({
                authorName: activityItem.author?.name,
                authorRole: activityItem.author?.author_role
              })}
            </Show>

            <Show if={activityItem.activity_type === "decision_made"}>
              {getAuthorText({
                authorName: activityItem.author?.name,
                authorRole: activityItem.author?.author_role
              })}

              {type.isPMS &&
                typeof activityItem.details === "object" &&
                "send_applicants_to_pms" in activityItem.details &&
                activityItem.details.send_applicants_to_pms !== undefined && (
                  <p className="text-sm font-normal text-content-400 mt-2">
                    {activityItem.details.send_applicants_to_pms
                      ? "Application was sent to Property Management System"
                      : "Application was not sent to Property Management System"}
                  </p>
                )}
            </Show>

            <Show
              if={
                activityItem.activity_type ===
                "push_applicants_to_property_management_system"
              }
            >
              {typeof activityItem.details === "object" &&
                "application_external_id" in activityItem.details &&
                activityItem.details.application_external_id && (
                  <p className="text-sm font-normal text-content-400">
                    External ID: {activityItem.details.application_external_id}
                  </p>
                )}
              {typeof activityItem.details === "object" &&
              "error_message" in activityItem.details &&
              activityItem.details.error_message ? (
                <p className="text-sm font-normal text-danger-600 mt-2">
                  Error: {activityItem.details.error_message}
                </p>
              ) : null}
            </Show>

            <Show
              if={
                activityItem.activity_type === "appfolio_guest_card_created" ||
                activityItem.activity_type === "appfolio_guest_card_updated"
              }
            >
              {typeof activityItem.details === "object" &&
              "guest_card_url" in activityItem.details &&
              activityItem.details.guest_card_url ? (
                <p className="text-sm font-normal text-content-400">
                  Guest Card:{" "}
                  <a
                    href={activityItem.details.guest_card_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 underline break-all"
                  >
                    {activityItem.details.guest_card_url}
                  </a>
                </p>
              ) : null}
              {typeof activityItem.details === "object" &&
              "error_message" in activityItem.details &&
              activityItem.details.error_message ? (
                <p className="text-sm font-normal text-danger-600 mt-2">
                  Reason: {activityItem.details.error_message}
                </p>
              ) : null}
            </Show>

            <Show
              if={
                activityItem.activity_type ===
                "appfolio_guest_card_already_exists"
              }
            >
              {typeof activityItem.details === "object" &&
              "guest_card_external_id" in activityItem.details &&
              activityItem.details.guest_card_external_id ? (
                <p className="text-sm font-normal text-content-400">
                  Matched existing card:{" "}
                  {"guest_card_url" in activityItem.details &&
                  activityItem.details.guest_card_url ? (
                    <a
                      href={activityItem.details.guest_card_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 underline"
                    >
                      {activityItem.details.guest_card_external_id}
                    </a>
                  ) : (
                    activityItem.details.guest_card_external_id
                  )}
                </p>
              ) : null}
              {typeof activityItem.details === "object" &&
              "message" in activityItem.details &&
              activityItem.details.message ? (
                <p className="text-sm font-normal text-content-400 mt-2">
                  {activityItem.details.message}
                </p>
              ) : null}
            </Show>

            {["canceled", "uncanceled", "invite_canceled"].includes(
              activityItem.activity_type
            ) && activityItem.author?.name ? (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author.name} (
                {activityItem.author.author_role
                  ? RoleTitles[activityItem.author.author_role]
                  : "--"}
                )
              </p>
            ) : null}

            {activityItem.activity_type ===
              "failed_identity_verification_manual_override" && (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author?.name} (
                {RoleTitles[activityItem.author?.author_role || "--"]})
              </p>
            )}

            {activityItem.activity_type ===
              "failed_warnings_report_manual_override" && (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author?.name} (
                {RoleTitles[activityItem.author?.author_role || "--"]})
              </p>
            )}

            {activityItem.activity_type === "role_changed" && (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author?.name} (
                {RoleTitles[activityItem.author?.author_role || ""]})
              </p>
            )}

            {activityItem.activity_type === "report_run_manually" && (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author?.name} (
                {RoleTitles[activityItem.author?.author_role || ""]})
              </p>
            )}

            {activityItem.activity_type === "verified_income_adjusted" && (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author?.name} (
                {RoleTitles[activityItem.author?.author_role || ""]})
              </p>
            )}

            {activityItem.activity_type === "invited" && (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author?.name}{" "}
                {activityItem.author?.author_role &&
                activityItem.author.author_role in RoleTitles
                  ? `(${RoleTitles[activityItem.author.author_role]})`
                  : ""}
              </p>
            )}

            {(activityItem.activity_type === "landlord_survey_sent" ||
              activityItem.activity_type === "landlord_survey_manual_resent" ||
              activityItem.activity_type === "landlord_survey_first_nudge" ||
              activityItem.activity_type === "landlord_survey_second_nudge" ||
              activityItem.activity_type === "landlord_survey_final_nudge") && (
              <LandlordSurveyView
                activityItem={activityItem as ApplicationActivity}
              />
            )}

            <Show
              if={["discount_applied", "discount_removed"].includes(
                activityItem.activity_type
              )}
            >
              {typeof activityItem.details === "object" &&
                "discount_amount" in activityItem.details && (
                  <p className="text-sm font-normal text-content-400">
                    Discount:{" "}
                    {formatToCurrency({
                      cents: (activityItem as DiscountActivity).details
                        .discount_amount.cents,
                      currency: (activityItem as DiscountActivity).details
                        .discount_amount.currency
                    })}
                    . Discount code:{" "}
                    {getDiscountCodeName(
                      (activityItem as DiscountActivity).details.discount_code
                    )}
                  </p>
                )}
            </Show>

            <Show
              if={
                activityItem.activity_type ===
                "verification_template_overridden"
              }
            >
              <VerificationTemplateOverride
                activityItem={
                  activityItem as VerificationTemplateOverriddenActivity
                }
              />
            </Show>

            <Show
              if={
                activityItem.activity_type === "verification_template_changed"
              }
            >
              <VerificationTemplateChanged
                activityItem={
                  activityItem as VerificationTemplateChangedActivity
                }
              />
            </Show>

            <Show if={activityItem.activity_type === "template_route_answered"}>
              <TemplateRouteAnswered
                activityItem={activityItem as TemplateRouteAnsweredActivity}
              />
            </Show>
          </>
        ) : null}

        {activityItem.activity_type === "desired_move_in_date_updated" ? (
          <div className="space-y-2">
            <p className="text-base text-content-1000">
              <span className="font-bold">
                Desired move-in date was changed
              </span>
              {activityItem.author?.name ? (
                <span>
                  {" "}
                  by {activityItem.author.name}
                  {activityItem.author.author_role
                    ? ` (${RoleTitles[activityItem.author.author_role]})`
                    : null}
                </span>
              ) : null}
            </p>
            <div className="text-sm font-normal text-content-400">
              <p>
                {(activityItem as DesiredMoveInDateUpdatedActivity).details
                  .previous_desired_move_in_date ? (
                  <>
                    Previous move-in date{" "}
                    {moment(
                      (activityItem as DesiredMoveInDateUpdatedActivity).details
                        .previous_desired_move_in_date
                    ).format("MMM DD, YYYY")}{" "}
                    →{" "}
                  </>
                ) : null}
                New move-in date{" "}
                {moment(
                  (activityItem as DesiredMoveInDateUpdatedActivity).details
                    .desired_move_in_date
                ).format("MMM DD, YYYY")}
              </p>
            </div>
          </div>
        ) : null}
        {activityItem.activity_type === "desired_lease_term_updated" ? (
          <div className="space-y-2">
            <p className="text-base text-content-1000">
              <span className="font-bold">Desired lease term was edited</span>
              {activityItem.author?.name ? (
                <span>
                  {" "}
                  by {activityItem.author.name}
                  {activityItem.author.author_role
                    ? ` (${RoleTitles[activityItem.author.author_role]})`
                    : null}
                </span>
              ) : null}
            </p>
            <div className="text-sm font-normal text-content-400">
              <p>
                {(activityItem as DesiredLeaseTermUpdatedActivity).details
                  .previous_desired_lease_term ? (
                  <>
                    Previous lease term{" ("}
                    {
                      (activityItem as DesiredLeaseTermUpdatedActivity).details
                        .previous_desired_lease_term
                    }
                    {` month${activityItem.details.previous_desired_lease_term === 1 ? "" : "s"} ) `}
                    →{" "}
                  </>
                ) : null}
                New lease term{" ("}
                {
                  (activityItem as DesiredLeaseTermUpdatedActivity).details
                    .desired_lease_term
                }
                {` month${activityItem.details.desired_lease_term === 1 ? "" : "s"})`}
              </p>
            </div>
          </div>
        ) : null}
        {activityItem.activity_type === "split_application" ||
        activityItem.activity_type === "created_from_split" ? (
          <ApplicationSplit
            activityItem={activityItem as SplitApplicationActivity}
          />
        ) : null}
        {activityItem.activity_type === "application_assigned" ? (
          <div className="space-y-2">
            <p className="text-base font-bold text-content-1000">
              {(activityItem as ApplicationAssignedActivity).details
                .new_assignees?.length
                ? `Rental application assigned to ${(activityItem as ApplicationAssignedActivity).details.new_assignees?.map((a) => a.name).join(", ")}`
                : "Rental application unassigned"}
            </p>
            {activityItem.author?.name && (
              <p className="text-sm font-normal text-content-400">
                {(activityItem as ApplicationAssignedActivity).details
                  .new_assignees?.length
                  ? "Assigned"
                  : "Unassigned"}{" "}
                by: {activityItem.author.name}
              </p>
            )}
          </div>
        ) : null}
        {activityItem.activity_type === "stage_changed_manual" ||
        activityItem.activity_type === "stage_changed_auto" ? (
          <ApplicationStageChanged activityItem={activityItem} />
        ) : null}
        {activityItem.activity_type === "application_merged" ? (
          <ApplicationMerged
            activityItem={activityItem as ApplicationMergedActivity}
          />
        ) : null}
        {activityItem.activity_type === "application_moved_to_new_unit" ? (
          <div className="space-y-2">
            <p className="text-base text-content-1000">
              <span className="font-bold">Application has been moved</span>
              {activityItem.author?.name ? (
                <span>
                  {" "}
                  by {activityItem.author.name}
                  {activityItem.author.author_role
                    ? ` (${RoleTitles[activityItem.author.author_role]})`
                    : null}
                </span>
              ) : null}
            </p>
            <div className="text-sm font-normal text-content-400">
              <p>
                New address:{" "}
                {[
                  (activityItem as ApplicationMovedActivity).details
                    .property_name,
                  (activityItem as ApplicationMovedActivity).details
                    .community_name,
                  (activityItem as ApplicationMovedActivity).details
                    .unit_address
                ]
                  .filter(Boolean)
                  .join(" - ")}
              </p>
              <p>
                Previous address:{" "}
                {[
                  (activityItem as ApplicationMovedActivity).details
                    .previous_property_name,
                  (activityItem as ApplicationMovedActivity).details
                    .previous_community_name,
                  (activityItem as ApplicationMovedActivity).details
                    .previous_unit_address
                ]
                  .filter(Boolean)
                  .join(" - ")}
              </p>
            </div>
          </div>
        ) : null}
        <Show if={activityItem.activity_type.includes("application_rent")}>
          <AdjustedApplicationRentLog
            activityItem={activityItem as AdjustedApplicationRentActivity}
          />
        </Show>
        <Show if={activityItem.activity_type === "applicant_pet_rent_adjusted"}>
          <AdjustedApplicantFeeOverrides
            activityItem={activityItem as AdjustedApplicantFeeActivity}
          />
        </Show>

        <Show if={activityItem.activity_type === "itemized_receipt_sent"}>
          <ItemizedReceiptSent
            activity={activityItem as ItemizedReceiptSentActivity}
          />
        </Show>

        {AGENT_ACTIVITY_TYPES.includes(
          activityItem.activity_type as (typeof AGENT_ACTIVITY_TYPES)[number]
        ) ? (
          <AgentActivityCard activityItem={activityItem as AgentActivity} />
        ) : null}
        {activityItem.activity_type === "underwriting_agent_check_completed" ? (
          <UnderwritingAgentCheckCard activity={activityItem} />
        ) : null}
        <Show
          if={
            activityItem.activity_type ===
            "applicant_housing_assistance_adjusted"
          }
        >
          <HousingAssistanceAdjusted
            activity={activityItem as HousingAssistanceAdjustedActivity}
          />
        </Show>

        <Show if={activityItem.activity_type === "income_finished_by_override"}>
          <IncomeOverride activity={activityItem as IncomeOverrideActivity} />
        </Show>
      </Card>

      <p className="flex justify-end mt-2 text-sm text-content-300 mr-4">
        {moment(activityItem.created_at).format("MMM DD, YYYY [at] h:mm A")}
      </p>
    </div>
  );
};
